import React, { useContext } from 'react';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { AssetHelper } from '../helper/assetHelper';

import { SvgImage, Image } from './Image';
import { FeatureAppContext } from '../context/FeatureAppContext';
import { AssetSlideProps } from './ComponentTypes';
import { StyledAssetSlide } from './Styles';
import StageVideo from './StageVideo';
import { AssetTypeVideo } from '../AssetTypes';
import { Asset } from '../EditorContentTypes';
import { useI18n } from '../i18n';

function isAssetTypeVideo(asset: Asset): asset is AssetTypeVideo {
  return asset.assetType === 'video';
}

const AssetSlide = React.forwardRef<HTMLDivElement, AssetSlideProps>(
  ({ active, content: { asset }, backgroundColor, index }, ref) => {
    const additionalStyles = {
      ...(backgroundColor ? { backgroundColor } : null),
      ...(!active ? { display: 'none' } : null),
    };
    const { logger } = useContext(FeatureAppContext);
    const assetSlideAria = useI18n('assetSlideAria');

    return (
      <StyledAssetSlide
        style={additionalStyles}
        active={active}
        tabIndex={active ? 0 : -1}
        aria-hidden={!active}
        ref={ref}
        role="group"
        aria-roledescription={assetSlideAria}
      >
        <UeContainer
          as="span"
          propertyPath={`slides.${index}.asset`}
          label="Asset"
          key={`container_asset_.${index}`}
        >
          <UeReference
            key={`ref_asset_.${index}`}
            propertyPath={`slides.${index}.asset`}
            behavior="component"
          >
            {asset && asset.assetType && asset.assetType === 'svg' && (
              <UeElement
                type="media"
                property="source"
                label="SvgImage"
                key={`element_asset_.${index}`}
              >
                {(ueElemProps) => {
                  return (
                    <SvgImage
                      src={AssetHelper.getImageUrl(asset.source?.assetValue, logger)}
                      alt={active ? asset.alt : ''}
                      testId="image"
                      loading="lazy"
                      {...ueElemProps}
                    />
                  );
                }}
              </UeElement>
            )}
            {asset && asset.assetType && asset.assetType === 'image' && (
              <UeElement
                type="media"
                property="source"
                label="Image"
                key={`element_asset_.${index}`}
              >
                {(ueElemProps) => {
                  return (
                    <Image
                      url={AssetHelper.getImageUrl(asset!.source?.assetValue, logger)}
                      alt={active ? asset.alt : ''}
                      testId="image"
                      {...ueElemProps}
                    />
                  );
                }}
              </UeElement>
            )}
            {asset && asset.assetType && asset.assetType === 'video' && isAssetTypeVideo(asset) && (
              <UeElement
                type="media"
                property="source"
                label="Video"
                key={`element_asset_.${index}`}
              >
                {(ueElemProps) => (
                  <StageVideo index={index} asset={asset} active={active} {...ueElemProps} />
                )}
              </UeElement>
            )}
          </UeReference>
        </UeContainer>
      </StyledAssetSlide>
    );
  },
);

export default AssetSlide;
